import { Tooltip } from '@blueprintjs/core';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { KnownAccessControls } from 'app/business-logic/security/KnownAccessControls';
import { NoiseEvent } from 'app/business-logic/services/noise-service';
import { Checkbox } from 'app/components/checkbox';
import { FieldValidationError } from 'app/components/form/field-validation-error';
import { AccessControlGate } from 'app/routing/access-control-gate';
import { DATA_TRACKING_KEY } from 'app/views/components/heap-analytics';
import { useI18n } from 'core/hooks/useI18n';
import { useFormikContext } from 'formik';
import {
  ClassificationCommentsField,
  ClassificationCommentsWrapper,
  DisregardWrapper,
  EventClassificationHeading,
  HiddenLabel,
  OptionsWrapper,
} from '../EventClassification.styles';
import { EventClassificationState } from '../EventClassification.types';

export const EventClassificationComments = ({ event, isLoading }: { event: NoiseEvent; isLoading: boolean }) => {
  const { l10n } = useI18n('app/views/alerts', 'eventClassification');
  const { values, setFieldValue } = useFormikContext<EventClassificationState>();
  const commentLength = 1000;
  const isCommentTooLong = values.comments.length > commentLength;
  const setIsDisregarded = (isDisregarded: boolean) => {
    const key: keyof EventClassificationState = 'disregarded';
    const value: EventClassificationState[typeof key] = isDisregarded;
    setFieldValue(key, value);
  };
  const setComments = (comments: string) => {
    const key: keyof EventClassificationState = 'comments';
    const value: EventClassificationState[typeof key] = comments;
    setFieldValue(key, value);
  };

  return (
    <AccessControlGate requires={KnownAccessControls.app.noise.classification.data.write._}>
      <EventClassificationHeading>{l10n('heading.comments')}</EventClassificationHeading>
      <ClassificationCommentsWrapper>
        <HiddenLabel htmlFor={`comments-${event.id}`}>{l10n('labels.comments')}</HiddenLabel>
        <ClassificationCommentsField
          $invalid={isCommentTooLong}
          id={`comments-${event.id}`}
          value={values.comments ?? ''}
          onChange={e => {
            setComments(e.target.value);
          }}
          disabled={isLoading}
          placeholder={l10n('commentsPlaceholder')}
          data-tracking={DATA_TRACKING_KEY['event-history-comments']}
        />
        <OptionsWrapper>
          <DisregardWrapper>
            <Checkbox
              label={l10n('labels.disregardEvent')}
              checked={values.disregarded}
              disabled={isLoading}
              onChange={e => {
                setIsDisregarded(e.target.checked);
              }}
              data-tracking={DATA_TRACKING_KEY['event-history-disregard-button']}
            />
            <Tooltip content={l10n('tooltips.disregardEvent')} placement="right">
              <FontAwesomeIcon
                icon={faInfoCircle}
                data-tracking={DATA_TRACKING_KEY['event-history-disregard-tooltip']}
              />
            </Tooltip>
          </DisregardWrapper>
        </OptionsWrapper>
      </ClassificationCommentsWrapper>
      {isCommentTooLong && (
        <FieldValidationError>{l10n('validation.length', { length: commentLength })}</FieldValidationError>
      )}
    </AccessControlGate>
  );
};
